import { useTheme } from '@shopify/restyle';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';

import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { useOptimisticAtomState } from 'app/hooks/use-optimistic-state';
import type { Profile } from 'app/network/profile';
import { useUpsertMyProfile } from 'app/network/profile';
import { Placeholder, PlaceholderLine, ShineOverlay } from 'app/overrides/rn-placeholder';
import type { ImagePickerSuccessResult } from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { optimisticUserProfilePictureAtom } from 'app/atoms/ui';
import { showFileTooLargeToastIfNeeded } from 'app/lib/upload-errors';
import type { Theme } from 'app/shopify';
import { IconPencil } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import Toast from 'react-native-toast-message';

interface SectionProps {
  profile?: Profile;
}

export function AvatarSection({ profile }: SectionProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('profile-overview');
  const { mutate: updateProfile, isPending } = useUpsertMyProfile();
  const [userProfilePicture, setUserProfilePicture] = useOptimisticAtomState(
    optimisticUserProfilePictureAtom,
    profile?.picture,
  );

  // Optimistically update the image URI when the user selects a new photo
  const onAvatarChanged = (info: ImagePickerSuccessResult) => {
    const newImageURI = info.assets[0]?.uri;

    if (newImageURI) {
      // Optimistically update the image URI
      // setUserProfilePicture(newImageURI);

      // Perform the mutation to update the profile picture
      updateProfile(
        { picture: info.assets[0] },
        {
          onSuccess: () => {
            setTimeout(() => {
              Toast.show({
                type: 'success',
                position: 'bottom',
                bottomOffset: 100,
                text1: t('profilePictureChangedToast'),
              });
            }, 300);
          },
          onError: (error) => {
            showFileTooLargeToastIfNeeded(error);
            console.error('Failed to update profile picture:', error);
          },
          onSettled: () => {
            setUserProfilePicture(undefined);
          },
        },
      );
    }
  };

  const { onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(onAvatarChanged, undefined, {
    aspect: [1, 1],
    allowsMultipleSelection: false,
  });

  if (isPending)
    return (
      <Column height={100} alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color={theme.colors['icon.brand']} />
      </Column>
    );

  return (
    <>
      <View
        style={{
          backgroundColor: theme.colors['surface.default'],
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          paddingTop: theme.spacing['spacing.general.x6'],
          paddingBottom: theme.spacing['spacing.general.x4'],
          alignItems: 'center',
        }}
      >
        {!profile ? (
          <Placeholder Animation={ShineOverlay}>
            <PlaceholderLine
              style={{
                height: 100,
                width: 70,
                borderRadius: theme.borderRadii['radius.md'],
                aspectRatio: 1,
                alignSelf: 'center',
              }}
            />
          </Placeholder>
        ) : (
          <Column gap="spacing.general.x3" alignItems="center">
            <Pressable
              accessibilityLabel={t('avatar.edit', { ns: 'accessibility' })}
              accessibilityRole="button"
              onPress={() => onUseImagePickerCamera()}
            >
              <View>
                <Thumbnail
                  alignSelf="center"
                  hideEditButton
                  onChange={onAvatarChanged}
                  source={{ uri: userProfilePicture }}
                />
                <View
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: theme.colors['text.primary'],
                    borderRadius: 16,
                    width: 32,
                    height: 32,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: theme.colors['surface.default'],
                  }}
                >
                  <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['surface.default']} />
                </View>
              </View>
            </Pressable>

            {profile.firstName ? (
              <Text variant="bodyLarge" fontWeight="700">
                {[profile.firstName, profile.lastName].filter(Boolean).join(' ')}
              </Text>
            ) : null}

            {profile.additionalName ? (
              <Text color="text.placeholder" textAlign="center" variant="bodyBase">
                {profile.additionalName}
              </Text>
            ) : null}
          </Column>
        )}
      </View>

      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </>
  );
}

interface ButtonEditProfileAvatarProps {
  profile?: Profile;
}

export function useEditProfileAvatar(profile?: Profile) {
  const { mutate: updateProfile, isPending } = useUpsertMyProfile();
  const { t } = useTranslation('profile-overview');

  const [, setUserProfilePicture] = useOptimisticAtomState(optimisticUserProfilePictureAtom, profile?.picture);

  const onAvatarChanged = (info: ImagePickerSuccessResult) => {
    const newImageURI = info.assets[0]?.uri;

    if (newImageURI) {
      // Optimistically update the image URI
      setUserProfilePicture(newImageURI);

      // Perform the mutation to update the profile picture
      updateProfile(
        { picture: info.assets[0] },
        {
          onSuccess: () => {
            setTimeout(() => {
              Toast.show({
                type: 'success',
                position: 'bottom',
                bottomOffset: 100,
                text1: t('profilePictureChangedToast'),
              });
            }, 300);
          },
          onError: (error) => {
            showFileTooLargeToastIfNeeded(error);
            console.error('Failed to update profile picture:', error);
          },
          onSettled: () => {
            setUserProfilePicture(undefined);
          },
        },
      );
    }
  };

  const imagePicker = useImagePickerCamera(onAvatarChanged, undefined, {
    allowsEditing: true,
    aspect: [1, 1],
    allowsMultipleSelection: false,
  });

  return { ...imagePicker, isPending };
}

export function ButtonEditProfileAvatar({ profile }: ButtonEditProfileAvatarProps) {
  const { t } = useTranslation('profile-overview');
  const { onUseImagePickerCamera } = useEditProfileAvatar(profile);

  return (
    <Button
      alignItems="center"
      onPress={() => onUseImagePickerCamera()}
      style={{ height: 40 }}
      variant="secondaryLight"
      flex={1}
    >
      <Row alignItems="center" gap="spacing.general.x1">
        <Text variant="bodyBase">{t('editAvatarButton')}</Text>
        <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={24} />
      </Row>
    </Button>
  );
}
